import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { holidaysService } from '../services/api';
import { Calendar, Plus, Edit, Trash2, Save, X, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal.jsx';

const HolidaysManagement = () => {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [confirmState, setConfirmState] = useState({ show: false, holidayId: null, holidayName: '' });
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    deduct_hours: false,
  });

  // Buscar feriados
  const { data: holidaysData, isLoading, refetch } = useQuery({
    queryKey: ['holidays', selectedYear],
    queryFn: () => holidaysService.getHolidays({ year: selectedYear }),
    staleTime: 0,
  });

  const holidays = React.useMemo(() => {
    if (!holidaysData?.data?.data) return [];
    return Array.isArray(holidaysData.data.data) ? holidaysData.data.data : [];
  }, [holidaysData]);

  // Mutação para criar feriado
  const createMutation = useMutation({
    mutationFn: (data) => holidaysService.createHoliday(data),
    onSuccess: () => {
      toast.success('Feriado criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar feriado');
    },
  });

  // Mutação para atualizar feriado
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => holidaysService.updateHoliday(id, data),
    onSuccess: () => {
      toast.success('Feriado atualizado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
      setIsModalOpen(false);
      setEditingHoliday(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar feriado');
    },
  });

  // Mutação para deletar feriado
  const deleteMutation = useMutation({
    mutationFn: (id) => holidaysService.deleteHoliday(id),
    onSuccess: () => {
      toast.success('Feriado deletado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['holidays'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar feriado');
    },
  });

  const resetForm = () => {
    setFormData({
      date: '',
      name: '',
      deduct_hours: false,
    });
    setEditingHoliday(null);
  };

  const handleOpenModal = (holiday = null) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setFormData({
        date: holiday.date.split('T')[0],
        name: holiday.name,
        deduct_hours: holiday.deduct_hours || false,
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingHoliday(null);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.date || !formData.name) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const submitData = {
      date: formData.date,
      name: formData.name,
      deduct_hours: formData.deduct_hours,
    };

    if (editingHoliday) {
      updateMutation.mutate({ id: editingHoliday.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (holiday) => {
    setConfirmState({ show: true, holidayId: holiday.id, holidayName: holiday.name });
  };

  const handleConfirmDelete = () => {
    if (confirmState.holidayId) {
      deleteMutation.mutate(confirmState.holidayId);
    }
    setConfirmState({ show: false, holidayId: null, holidayName: '' });
  };

  const handleCancelDelete = () => {
    setConfirmState({ show: false, holidayId: null, holidayName: '' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[date.getDay()];
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Gerenciamento de Feriados
        </h1>
        <p className="text-gray-600 mt-1">
          Cadastre os feriados do ano e configure se as horas não trabalhadas serão descontadas do salário
        </p>
      </div>

      {/* Filtros e botão de adicionar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="block text-sm font-medium text-gray-700">
              Ano:
            </label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              min="2020"
              max="2100"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Adicionar Feriado
          </button>
        </div>
      </div>

      {/* Lista de feriados */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Carregando...
        </div>
      ) : holidays.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Nenhum feriado cadastrado para {selectedYear}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dia da Semana
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome do Feriado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descontar Horas
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {holidays.map((holiday) => (
                <tr key={holiday.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatDate(holiday.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {getDayName(holiday.date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {holiday.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {holiday.deduct_hours ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Sim
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Não
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenModal(holiday)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(holiday)}
                        className="text-red-600 hover:text-red-900"
                        title="Deletar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de criar/editar feriado */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingHoliday ? 'Editar Feriado' : 'Novo Feriado'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data do Feriado *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Feriado *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Natal, Ano Novo, etc."
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="deduct_hours"
                      checked={formData.deduct_hours}
                      onChange={(e) => setFormData({ ...formData, deduct_hours: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="deduct_hours" className="ml-2 text-sm text-gray-700">
                      Descontar horas não trabalhadas do cálculo de salário
                    </label>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Importante:</strong> Se marcado, as horas que normalmente seriam trabalhadas neste dia serão descontadas do salário do funcionário no holerite.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {createMutation.isLoading || updateMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmState.show}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja deletar o feriado "${confirmState.holidayName}"?`}
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default HolidaysManagement;
